import { useCallback } from 'react';
import { atom, useAtom } from 'jotai';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import { createId } from '@/lib/id';

/* ── Types ── */
export type ToastVariant = 'success' | 'error' | 'info' | 'warning';

export interface Toast {
  id: string;
  message: string;
  variant: ToastVariant;
}

/* ── Atom ── */
export const toastsAtom = atom<Toast[]>([]);

/* ── Helpers ── */
const appendToast = (t: Toast) => (prev: Toast[]) => [...prev, t];
const removeToast = (id: string) => (prev: Toast[]) => prev.filter((t) => t.id !== id);

/* ── Hook ── */
export function useToast() {
  const [, setToasts] = useAtom(toastsAtom);

  const toast = useCallback(
    (message: string, variant: ToastVariant = 'info') => {
      const id = createId();
      setToasts(appendToast({ id, message, variant }));
      setTimeout(() => setToasts(removeToast(id)), 4000);
    },
    [setToasts]
  );

  return { toast };
}

/* ── Container ── */
export function ToastContainer() {
  const [toasts, setToasts] = useAtom(toastsAtom);
  const dismiss = (id: string) => setToasts((prev) => prev.filter((t) => t.id !== id));

  return (
    <Snackbar
      open={toasts.length > 0}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      sx={{
        top: { xs: 16, sm: 24 },
        // Keep true horizontal center: width:100% on the content breaks MUI's
        // left:50% + translateX(-50%) centering (toast looks left-shifted in sheets).
        left: '50%',
        right: 'auto',
        transform: 'translateX(-50%)',
        width: 'auto',
        maxWidth: 'calc(100vw - 32px)',
        // Above nested dialogs / account DetailSectionSheet.
        zIndex: (theme) => theme.zIndex.modal + 50,
      }}
    >
      <Stack spacing={1} sx={{ minWidth: 280, maxWidth: 400, width: 'max-content' }}>
        {toasts.map((t) => (
          <Alert
            key={t.id}
            severity={t.variant}
            variant="filled"
            role={t.variant === 'error' || t.variant === 'warning' ? 'alert' : 'status'}
            onClose={() => dismiss(t.id)}
            sx={{
              borderRadius: 2.5,
              boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
              fontSize: '0.8125rem',
              width: '100%',
            }}
          >
            {t.message}
          </Alert>
        ))}
      </Stack>
    </Snackbar>
  );
}
